// Ireland timezone helper
// Ireland uses Europe/Dublin timezone (IST - Irish Standard Time in summer, GMT in winter)

/**
 * Get the current time in Ireland timezone
 * @returns {Date} Current time in Ireland timezone
 */
export const getIrelandTime = () => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Dublin" }));
};

/**
 * Convert a UTC date to Ireland local time
 * @param {Date|string} utcDate - The UTC date
 * @returns {Date} The date in Ireland local time
 */
export const toIrelandTime = (utcDate) => {
  if (!utcDate) return null;
  const date = new Date(utcDate);
  
  // Get Ireland time string and parse it back
  const irelandString = date.toLocaleString("en-US", { timeZone: "Europe/Dublin" });
  return new Date(irelandString);
};

/**
 * Convert Ireland local time to UTC
 * @param {Date|string} irelandDate - The date in Ireland timezone
 * @returns {Date} The date in UTC
 */
export const fromIrelandTime = (irelandDate) => {
  if (!irelandDate) return null;
  
  // Parse the Ireland local date
  const date = new Date(irelandDate);
  
  // Create UTC equivalent by treating it as local and adjusting
  const irelandString = date.toLocaleString("en-US", { timeZone: "Europe/Dublin" });
  const utcEquivalent = new Date(irelandString);
  
  // Calculate the offset
  const offset = date - utcEquivalent;
  
  return new Date(date - offset);
};

/**
 * Format a date/time string in Ireland timezone
 * @param {Date|string} date - The date to format
 * @param {string} format - 'date', 'time', or 'datetime'
 * @returns {string} Formatted string
 */
export const formatIrelandTime = (date, format = 'time') => {
  if (!date) return "-";
  
  const irelandDate = toIrelandTime(date);
  
  if (format === 'date') {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Europe/Dublin"
    }).format(irelandDate);
  }
  
  if (format === 'time') {
    const hours = String(irelandDate.getHours()).padStart(2, "0");
    const minutes = String(irelandDate.getMinutes()).padStart(2, "0");
    const seconds = String(irelandDate.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }
  
  // datetime
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Dublin"
  }).format(irelandDate);
};

/**
 * Get Ireland timezone offset in hours
 * @returns {number} Offset in hours from UTC
 */
export const getIrelandOffset = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const irelandTime = new Date(utc + (1 * 3600000)); // IST is UTC+1, GMT is UTC+0
  
  // Check if DST is active (roughly March-October)
  const month = now.getMonth();
  const isDST = month >= 2 && month <= 9;
  
  return isDST ? 1 : 0;
};

/**
 * Combine date and time strings and convert to UTC for database storage
 * @param {string} dateStr - Date in format YYYY-MM-DD (Ireland local)
 * @param {string} timeStr - Time in format HH:mm (Ireland local)
 * @returns {string} ISO string in UTC
 */
export const combineIrelandDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  
  // Parse the date and time
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  
  // Create date as if it were in Ireland timezone
  // We need to create a date string that represents Ireland local time
  const irelandString = `${dateStr}T${timeStr}:00`;
  
  // Get the current timezone offset
  const irelandDate = new Date(irelandString);
  
  // Convert to UTC by treating as Ireland local time
  // Create a date and manually set time components
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
  
  // Apply Ireland timezone offset
  const offset = getIrelandOffset();
  utcDate.setHours(utcDate.getHours() - offset);
  
  return utcDate.toISOString();
};

export default {
  getIrelandTime,
  toIrelandTime,
  fromIrelandTime,
  formatIrelandTime,
  getIrelandOffset,
  combineIrelandDateTime
};

